import { useEffect, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { ApiError } from '../../../../../shared/shared.api';
import { toWorkshopInput, type WorkshopFormValues } from '../policies/workshop-form.policy';
import { workOrderNumber } from '../policies/work-order-labels';
import { getWorkOrderDetails, recordWorkOrderDetails, type WorkOrderDetailsView } from '../work-orders.api';

export type WorkshopState = { status: 'loading' } | { status: 'loaded'; details: WorkOrderDetailsView | null } | { status: 'forbidden' } | { status: 'error' };

/** The newest recorded workshop details, and recording a new revision. A refusal stays inline in the panel; a recorded revision toasts. */
export function useWorkshopDetails(order: { id: string; sequenceNumber: number }) {
  const [state, setState] = useState<WorkshopState>({ status: 'loading' });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    getWorkOrderDetails(order.id)
      .then((details) => !cancelled && setState({ status: 'loaded', details }))
      .catch((err) => !cancelled && setState({ status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' }));
    return () => {
      cancelled = true;
    };
  }, [order.id]);

  async function save(values: WorkshopFormValues): Promise<boolean> {
    const built = toWorkshopInput(values);
    if (!built.ok) {
      setError(built.reason);
      return false;
    }
    setError(null);
    setSaving(true);
    try {
      setState({ status: 'loaded', details: await recordWorkOrderDetails(order.id, built.input) });
      toast.success(`Workshop details saved for ${workOrderNumber(order.sequenceNumber)}`);
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Could not save the workshop details — try again.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  return { state, error, saving, save, clearError: () => setError(null) };
}
